import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Cpu, 
  Bot, 
  Terminal, 
  Database, 
  KeyRound, 
  ArrowRight, 
  CheckCircle2 
} from 'lucide-react';

export function Home() {
  const archetypes = [
    {
      title: 'Single Page App (SPA)',
      badge: 'Interactive',
      badgeColor: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
      description: 'Public clients with authorization code flow, mandatory PKCE, and automated CORS origin synchronization.',
      features: ['PKCE Enforced (S256)', 'CORS Auto-Sync', 'No Client Secrets'],
      icon: KeyRound,
      linkText: 'Configure SPAs'
    },
    {
      title: 'Machine-to-Machine (M2M)',
      badge: 'Client Credentials',
      badgeColor: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/30',
      description: 'Backend services and daemons authenticating via salted SHA-256/512 hashed secrets with strict scope governance.',
      features: ['Salted SHA-512 Secrets', 'Zero Redirect URIs', 'Scoped Resource Access'],
      icon: Cpu,
      linkText: 'Manage Services'
    },
    {
      title: 'AI Agent / MCP Client',
      badge: 'RFC 8693 + DPoP',
      badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
      description: 'Model Context Protocol (MCP) clients with token exchange and proof-of-possession agent tool authorization.',
      features: ['RFC 8693 Token Exchange', 'DPoP Proof Verification', 'Semantic Tool Constraints'],
      icon: Bot,
      linkText: 'Inspect Agent Policies'
    }
  ];

  const invariants = [
    { name: 'Direct DbContext', detail: 'Zero generic repositories; LiteIdDbContext injected directly into route handlers' },
    { name: 'PostgreSQL JSON Native', detail: 'Native PrimitiveCollection and ToJson mappings for scopes, claims, and URIs' },
    { name: 'Runtime Isolation', detail: 'STS engine and Admin console run in distinct process boundaries with isolated credentials' },
    { name: 'Strict OAuth 2.1', detail: 'Implicit flow and ROPC disabled; exact match on redirect URIs strictly enforced' }
  ];

  return (
    <div className="space-y-8">
      {/* Top Banner / Hero */}
      <div className="bg-[#11131a] border border-slate-800 rounded-md p-6 sm:p-8 shadow-sm relative overflow-hidden">
        <div className="max-w-3xl">
          <div className="inline-flex items-center space-x-2 px-2.5 py-1 rounded bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 text-xs font-mono mb-4">
            <ShieldCheck className="w-4 h-4" />
            <span>Control Plane • .NET 10 + PostgreSQL 17</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-100 tracking-tight">
            LiteID Security Control Plane
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed">
            Enterprise-grade Identity Provider, Security Token Service (STS), and MCP-aware authorization control plane.
            Designed with non-generic architectures, passkey-first authentication, and fine-grained agentic tool authorization.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/fetch-data"
              className="inline-flex items-center space-x-2 rounded-md px-3.5 py-1.5 text-xs font-medium uppercase tracking-wide bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
            >
              <span>Test API Telemetry</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/counter"
              className="inline-flex items-center space-x-2 rounded-md px-3.5 py-1.5 text-xs font-medium uppercase tracking-wide bg-[#1a1d27] hover:bg-slate-800 text-slate-200 border border-slate-700 transition-colors"
            >
              <Terminal className="w-3.5 h-3.5 text-slate-400" />
              <span>Counter Diagnostics</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Archetype Cards Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider font-mono">
            Client Archetypes & Policy Engines
          </h2>
          <span className="text-xs text-slate-500 font-mono">RFC 8693 & OAuth 2.1 Ready</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {archetypes.map((arch) => {
            const Icon = arch.icon;
            return (
              <div
                key={arch.title}
                className="bg-[#11131a] border border-slate-800 rounded-md p-5 flex flex-col justify-between hover:border-slate-700 transition-all"
              >
                <div>
                  <div className="flex items-start justify-between">
                    <div className="p-2 rounded bg-[#1a1d27] border border-slate-800 text-indigo-400">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`text-[11px] font-mono px-2 py-0.5 rounded border ${arch.badgeColor}`}>
                      {arch.badge}
                    </span>
                  </div>

                  <h3 className="mt-4 text-base font-medium text-slate-100">
                    {arch.title}
                  </h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                    {arch.description}
                  </p>

                  <ul className="mt-4 space-y-2 border-t border-slate-800/80 pt-4">
                    {arch.features.map((feat) => (
                      <li key={feat} className="flex items-center space-x-2 text-xs text-slate-300">
                        <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 pt-3 border-t border-slate-800/60">
                  <span className="text-xs font-mono text-indigo-400 hover:text-indigo-300 inline-flex items-center space-x-1 cursor-pointer">
                    <span>{arch.linkText}</span>
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Architectural Invariants Overview */}
      <div className="bg-[#11131a] border border-slate-800 rounded-md p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Database className="w-4 h-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-slate-200 uppercase tracking-wider font-mono">
            System Invariants & Boundary Status
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {invariants.map((item) => (
            <div key={item.name} className="p-3.5 rounded bg-[#090a0f] border border-slate-800/80">
              <span className="text-xs font-mono font-medium text-slate-200 block">
                {item.name}
              </span>
              <span className="text-xs text-slate-400 mt-1 block">
                {item.detail}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
